import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import {
  accessPermission,
  AccessAction,
  AccessSectionId,
  firstAccessiblePath,
} from 'admin/core/auth/access-map';
import { CurrentUserService } from 'admin/core/auth/current-user.service';

/**
 * Lets a route through only when the user holds the permission `action` needs in `section`
 * (a section that has none for it, like the deferred recommendations one, is open). Otherwise
 * it redirects — silently — to the first section the user may view, or to `/` when there is
 * none; `/` is never guarded by this, so the redirect cannot loop. Runs after `authGuard`.
 */
export function permissionGuard(
  section: AccessSectionId,
  action: AccessAction = 'view',
): CanActivateFn {
  return () => {
    const currentUser = inject(CurrentUserService);
    const permission = accessPermission(section, action);

    if (permission === null || currentUser.can(permission)) {
      return true;
    }

    return inject(Router).createUrlTree([firstAccessiblePath(currentUser.permissions()) ?? '/']);
  };
}

/**
 * For the `/` route inside the layout: redirects to the first section the user may view, or
 * lets the «no access» page render when there is none.
 */
export const firstSectionGuard: CanActivateFn = () => {
  const path = firstAccessiblePath(inject(CurrentUserService).permissions());

  return path === null ? true : inject(Router).createUrlTree([path]);
};
